import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { HSE_CHECK_POINTS, INS_ACT_Checkbox_Types, GEN_TextField_Types, OPEN_CLOSE_OPTIONS } from "./generalIRConst";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { HSE_OPTIONS_LABEL } from "./hseCheckPoints";
import { generateGeneralIRPDF } from "./generalIRPdF";
import DownloadPdfLoaderModal from "../../../utils/commonComponent/downloadPdfLoader";
import { formateDate } from "../../../utils/commonComponent/commonConstant";
import { getPermissions } from "../rbac/hasAccess";


// section 5 open/close value
export const getSelectLabel = (options, value) => options?.find((o) => o.value == value)?.label || value || "";

const ViewGeneralIR = (props) => {
  const params = useParams();
  const printRef = React.useRef();
  const permissions = getPermissions(props.moduleId);

  const [inputStates, setInputStates] = React.useState(null);
  const [itemDetails, setItemDetails] = React.useState([]);
  const [applicableDocRows, setApplicableDocRows] = React.useState([]);
  const [areaOfConcernRows, setAreaOfConcernRows] = React.useState([]);
  const [detailsWitnessRows, setDetailsWitnessRows] = React.useState([]);
  const [detailsReviewedRows, setDetailsReviewedRows] = React.useState([]);
  const [detailsEquipmentRows, setDetailsEquipmentRows] = React.useState([]);
  const [hseData, setHseData] = React.useState({});
  const [uploadedImages, setUploadedImages] = React.useState([]);
  const [downloadPdfloading, setDownloadPdfloading] = React.useState(false);
  

  const HSE_LABEL = HSE_OPTIONS_LABEL.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});

  useEffect(() => {
    fetchAll();
  }, [params.id]);

  const fetchAll = async () => {
    const res = await fetchQuery(`select * from generalIR where id = ${params.id}`);
    setInputStates(res?.[0] || {});

    const items = await fetchQuery(`select * from GeneralInspectedItems where generalIRId = ${params.id}`);
    setItemDetails(items || []);

    const docs = await fetchQuery(`select * from generalReferenceDocuments where generalIRId = ${params.id}`);
    setApplicableDocRows(docs || []);

    const areas = await fetchQuery(`select * from generalAreaConcern where generalIRId = ${params.id}`);
    setAreaOfConcernRows(areas || []);

    const witness = await fetchQuery(`select * from generalInspectionWitnessed where generalIRId = ${params.id}`);
    setDetailsWitnessRows(witness || []);

    const reviewed = await fetchQuery(`select * from generalReviewedDoc where generalIRId = ${params.id}`);
    setDetailsReviewedRows(reviewed || []);

    const equipment = await fetchQuery(`select * from generalEquipmentUsed where generalIRId = ${params.id}`);
    setDetailsEquipmentRows(equipment || []);

    const hse = await fetchQuery(`select * from generalHseCheckPoints where generalIRId = ${params.id}`);
    if (hse?.[0]) {
      const { id, generalIRId, ...rest } = hse[0];
      setHseData(rest);
    }

    const photos = await fetchQuery(`select * from generalEvaluationPhotos where generalIRId = ${params.id}`);
    if (photos?.[0]) {
      const { imagename, imageFilePath } = photos[0];
      const names = imagename ? imagename.split("||") : [];
      const paths = imageFilePath ? imageFilePath.split(",").filter(Boolean) : [];
      setUploadedImages(paths.map((path, i) => ({ imagePath: path.trim(), imagesName: (names[i] || "").trim() })));
    }
  };

  if (!inputStates) return <div className="p-6 text-center text-gray-500">Loading...</div>;

  const cellCls = "border border-gray-400 px-2 py-1 text-md align-top";
  const thCls = "border border-gray-400 px-2 py-1 text-md font-semibold bg-[#F5F5F9] align-middle";

  const hseLeft = HSE_CHECK_POINTS.filter((_, i) => i % 2 === 0);
  const hseRight = HSE_CHECK_POINTS.filter((_, i) => i % 2 !== 0);

  return (
    <div>
      {/* Download Button - outside print area */}
      {(permissions.pdfDownload || permissions.allAccess) && (
      <div className="flex justify-end mb-4 gap-3">
        <button
          onClick={() =>
            generateGeneralIRPDF(
              inputStates,
              setDownloadPdfloading,
              itemDetails,
              applicableDocRows,
              areaOfConcernRows,
              detailsWitnessRows,
              detailsReviewedRows,
              detailsEquipmentRows,
              hseData,
              uploadedImages
            )
          }
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          Download PDF
        </button>
      </div>
      )}

      {/* Print Area */}
      <div ref={printRef} className="bg-white p-6 text-lg  mx-auto">
        {/* Header */}
        <div className="text-xl text-center font-semibold">
          INSPECTION REPORT
        </div>
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="text-md mt-1">
              <span className="font-semibold">Report Number – </span>
              {inputStates.reportNo || "—"}
            </div>
          </div>
          <img
            src="/logo.png"
            alt="DYC Logo"
            className="h-14 object-contain"
            onError={(e) => (e.target.style.display = "none")}
          />
        </div>

        {/* Section 1 & 2 */}
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={`${thCls} w-[65%] text-left`} colSpan={2}>
                1. General Information
              </th>
              <th className={`${thCls} w-[35%] text-left`}>
                2. Type of Inspection Activity:
                <span className="font-normal block text-[10px]">
                  (Select the Applicable Stage)
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {GEN_TextField_Types.map((field, i) => (
              <tr key={field.key}>
                <td className={cellCls}>
                  <div className="flex gap-1">
                    <span className="font-semibold whitespace-nowrap">
                      {field.label}
                      {field.subLabel && (
                        <span className="font-normal text-gray-500 text-[10px]">
                          {field.subLabel}
                        </span>
                      )}
                    </span>
                  </div>
                </td>
                <td className={cellCls}>
                  <div className="flex gap-1">
                    <span className="ml-2">{field.isDate ? formateDate(inputStates[field.key]) : (inputStates[field.key] || "")}</span>
                  </div>
                </td>
                <td className={cellCls}>
                  {INS_ACT_Checkbox_Types[i] && (
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block w-4 h-4 border border-gray-600 flex-shrink-0 ${
                          inputStates[INS_ACT_Checkbox_Types[i].key] == 1
                            ? "bg-gray-800"
                            : "bg-white"
                        }`}
                      >
                        {inputStates[INS_ACT_Checkbox_Types[i].key] == 1 && (
                          <span className="text-white text-[10px] flex items-center justify-center leading-none mt-[1px]">
                            ✓
                          </span>
                        )}
                      </span>
                      <span className="font-semibold">{INS_ACT_Checkbox_Types[i].label}</span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Section 3 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          3. Details of Inspected Items{" "}
          <span className="font-normal italic">
            (Fill the data as per PO/Offer list)
          </span>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={thCls}>PO Item No</th>
              <th className={thCls}>Item Code</th>
              <th className={thCls}>
                Item Description / Identification Number / MTC Number /
                Manufacturer
              </th>
              <th className={thCls}>P.O. Quantity with UOM</th>
              <th className={thCls}>Offered Quantity with UOM</th>
              <th className={thCls}>Acceptable Quantity with UOM</th>
            </tr>
          </thead>
          <tbody>
            {itemDetails.length > 0 ? (
              itemDetails.map((row, i) => (
                <tr key={i}>
                  <td className={cellCls}>{row.poItemNo || ""}</td>
                  <td className={cellCls}>{row.itemCode || ""}</td>
                  <td className={cellCls}>{row.itemDescription || ""}</td>
                  <td className={cellCls}>{row.poQuantityWithUom || ""}</td>
                  <td className={cellCls}>
                    {row.offeredQuantityWithUom || ""}
                  </td>
                  <td className={cellCls}>
                    {row.acceptableQuantityWithUom || ""}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className={`${cellCls} text-center text-gray-400`}
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Section 4 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          4. Applicable Reference Documents:{" "}
          <span className="font-normal italic">
            (Fill the applicable section and if not mention not applicable)
          </span>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={thCls}>Sr.No.</th>
              <th className={thCls}>Document Name</th>
              <th className={thCls}>Client Document Number</th>
              <th className={thCls}>Vendor Document Number</th>
              <th className={thCls}>Approval Status</th>
            </tr>
          </thead>
          <tbody>
            {applicableDocRows.length > 0 ? (
              applicableDocRows.map((row, i) => (
                <tr key={i}>
                  <td className={cellCls}>{i + 1}</td>
                  <td className={cellCls}>{row.documentName || ""}</td>
                  <td className={cellCls}>{row.clientDocumentNumber || ""}</td>
                  <td className={cellCls}>{row.vendorDocumentNumber || ""}</td>
                  <td className={cellCls}>{row.approvalStatus || ""}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className={`${cellCls} text-center text-gray-400`}
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Section 5 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          5. Area of Concern{" "}
          <span className="font-normal italic">
            (From This Visit & Previous visit, do not delete the concerns after
            closing)
          </span>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={thCls}>Sr. No.</th>
              <th className={thCls}>
                Area of Concern / Observations / Non - Conformities
              </th>
              <th className={thCls}>Date Initiated</th>
              <th className={thCls}>Open / Close</th>
              <th className={thCls}>Action By</th>
            </tr>
          </thead>
          <tbody>
            {areaOfConcernRows.length > 0 ? (
              areaOfConcernRows.map((row, i) => (
                <tr key={i} style={row.openClose === 2 ? { color: "#dc2626" } : {}}>
                  <td className={cellCls}>{i + 1}</td>
                  <td className={cellCls}>{row.areaOfConcern || ""}</td>
                  <td className={cellCls}>{formateDate(row.dateInitiated)}</td>
                  <td className={cellCls}>{getSelectLabel(OPEN_CLOSE_OPTIONS, row.openClose)}</td>
                  <td className={cellCls}>{row.actionBy || ""}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className={`${cellCls} text-center text-gray-400`}
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Section 6 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          6. Details of Inspection stages witnessed:{" "}
          <span className="font-normal italic">
            (Mention Witnessed activities as per scope – Findings of Inspection)
          </span>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={`${thCls} w-[15%]`}>QAP Stage</th>
              <th className={thCls}>Activities description as Per QAP</th>
            </tr>
          </thead>
          <tbody>
            {detailsWitnessRows.length > 0 ? (
              detailsWitnessRows.map((row, i) => (
                <tr key={i}>
                  <td className={cellCls}>{row.qapStage || ""}</td>
                  <td className={cellCls}>{row.activitiesDescription || ""}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={2}
                  className={`${cellCls} text-center text-gray-400`}
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Section 7 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          7. Details of Reviewed Documents:{" "}
          <span className="font-normal italic">
            (Mention reviewed documents as per scope with findings)
          </span>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={`${thCls} w-[15%]`}>QAP Stage</th>
              <th className={thCls}>Activities description as Per QAP</th>
            </tr>
          </thead>
          <tbody>
            {detailsReviewedRows.length > 0 ? (
              detailsReviewedRows.map((row, i) => (
                <tr key={i}>
                  <td className={cellCls}>{row.qapStage || ""}</td>
                  <td className={cellCls}>
                    {row.reviewedactivitiesDescription || ""}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={2}
                  className={`${cellCls} text-center text-gray-400`}
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Section 8 Result Summary */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          8. Result Summary:{" "}
          <span className="font-normal italic">
            (Mention Conclusive Result of Inspection here)
          </span>
        </div>
        <div className="border border-t-0 border-gray-400 px-2 py-2 text-md min-h-[30px]">
          {inputStates.resultSummary || ""}
        </div>

        {/* Section 9 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          9. Details of Equipment(s) / Instrument(s) used for Measuring /
          Testing during Inspection:
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={thCls}>Sr. No.</th>
              <th className={thCls}>Name of Equipment</th>
              <th className={thCls}>Range</th>
              <th className={thCls}>ID No.</th>
              <th className={thCls}>Calibration Due Date</th>
              <th className={thCls}>Calibration Certificate Number</th>
              <th className={thCls}>
                Traceability to NABL / ISO 17025 accredited lab (Yes/No)
              </th>
            </tr>
          </thead>
          <tbody>
            {detailsEquipmentRows.length > 0 ? (
              detailsEquipmentRows.map((row, i) => (
                <tr key={i}>
                  <td className={cellCls}>{i + 1}</td>
                  <td className={cellCls}>{row.nameOfEquipment || ""}</td>
                  <td className={cellCls}>{row.ranges || ""}</td>
                  <td className={cellCls}>{row.idNo || ""}</td>
                  <td className={cellCls}>{formateDate(row.calibrationDueDate)}</td>
                  <td className={cellCls}>{row.calibrationCertificateNumber || ""}</td>
                  <td className={cellCls}>{row.traceability || ""}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className={`${cellCls} text-center text-gray-400`}
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="border border-gray-400 px-2 py-1 text-[13px]">
          All above measuring instruments and equipment underwent a verification process to confirm their ongoing suitability for intended use, accurate identification, proper calibration status, and compliance with national standards, all of which were determined to be satisfactory.
        </div>

        {/* Section 10 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          10. Pending Activities:{" "}
          <span className="font-normal italic">
            (Write pending activities from this visit against offered scope /
            RFI)
          </span>
        </div>  
        <div className="border border-t-0 border-gray-400 px-2 py-2 text-md min-h-[30px] text-red-600">
          {inputStates.pendingActivities || ""}
        </div>

        {/* Section 11 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          11. Identification of the Inspected Items:{" "}
          <span className="font-normal italic">
            (Mention identification details where stamping has been done)
          </span>
        </div>
        <div className="border border-t-0 border-gray-400 px-2 py-2 text-md min-h-[30px]">
          {inputStates.identificationOfItems || ""}
        </div>

        {/* Section 12 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          12. Non-Conformity Report:{" "}
          <span className="font-normal italic">
            (Strike through options that are not applicable)
          </span>
        </div>
        <div className="border border-t-0 border-gray-400 px-2 py-2 text-md min-h-[30px]">
          {inputStates.nonConformity || ""}
        </div>

        {/* Section 13 */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          13. List of Attachments:{" "}
          <span className="font-normal italic">
            (Write short description of supporting documents to inspection)
          </span>
        </div>
        <div className="border border-t-0 border-gray-400 px-2 py-2 text-md min-h-[30px]">
          {inputStates.listOfAttachments || ""}
        </div>

        {/* Section 14 HSE */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          14. HSE check points:{" "}
          <span className="font-normal italic">
            (Write Yes / No / NA as applicable)
          </span>
        </div>
        <table className="w-full border-collapse">
          <tbody>
            {hseLeft.map((lp, i) => {
              const rp = hseRight[i];
              return (
                <tr key={i}>
                  <td className={`${cellCls} w-[35%]`}>{lp.label}</td>
                  <td
                    className={`${cellCls} w-[15%] text-center font-semibold`}
                  >
                    {hseData[lp.key] == 0
                      ? "-"
                      : HSE_LABEL[hseData[lp.key]] ?? "—"}
                  </td>
                  <td className={`${cellCls} w-[35%]`}>{rp ? rp.label : ""}</td>
                  <td
                    className={`${cellCls} w-[15%] text-center font-semibold`}
                  >
                    {rp
                      ? hseData[rp.key] == 0
                        ? "-"
                        : HSE_LABEL[hseData[rp.key]] ?? "—"
                      : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="border border-gray-400 px-2 py-1 text-[13px]">
          Safety requirements are evaluated by referencing Work Instruction
          DYC/INSP-WI/68 both before initiating and during the execution of
          inspection activities to ensure safety of working environment
        </div>

        {/* Section 15 Authorized Signatures */}
        <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1 mb-0">
          15. Authorized Signatures:
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={`${thCls} w-[50%] text-center`} colSpan={2}>
                DYC Global Pvt. Ltd. Surveyor:
              </th>
              <th className={`${thCls} w-[50%] text-center`} colSpan={2}>
                DYC Global Pvt. Ltd. Reviewer:
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={`${cellCls} text-center h-[100px]`} colSpan={2}>
                {inputStates.Surveyorsign && (
                  <img
                    src={inputStates.Surveyorsign}
                    alt="Surveyor"
                    className="max-h-20 object-contain mx-auto"
                  />
                )}
              </td>
              <td className={`${cellCls} text-center h-[100px]`} colSpan={2}>
                {inputStates.ReviewerSign && (
                  <img
                    src={inputStates.ReviewerSign}
                    alt="Reviewer"
                    className="max-h-20 object-contain mx-auto"
                  />
                )}
              </td>
            </tr>
            <tr>
              <td className={cellCls}>
                <span className="font-semibold">Name: {inputStates?.SurveyorName || ""}</span>
              </td>
              <td className={cellCls}>
                <span className="font-semibold">Date: </span>
                <span>{formateDate(inputStates?.SurveyorDate || "")}</span>
              </td>
              <td className={cellCls}>
                <span className="font-semibold">Name: {inputStates?.ReviewerName || ""}</span>
              </td>
              <td className={cellCls}>
                <span className="font-semibold">Date: </span>
                <span>{formateDate(inputStates?.ReviewerDate || "")}</span>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Section 16 Photos */}
        {uploadedImages.length > 0 && (
          <>
            <div className="font-semibold text-md bg-[#F5F5F9] border border-gray-400 px-2 py-1">
              16. Evaluation Photographs:
            </div>
            <div className="grid grid-cols-2 gap-2">
              {uploadedImages.map((img, i) => (
                <div key={i} className="border border-gray-600">
                  <img
                    src={img.imagePath}
                    alt={img.imagesName || `Photo ${i + 1}`}
                    className="w-full object-cover max-h-48 p-1"
                    crossOrigin="anonymous"
                  />
                  <div className="border-t border-gray-600 mt-1"></div>
                  {img.imagesName && (
                    <div className="text-center text-md py-1 border-t border-gray-300">
                      {img.imagesName}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {downloadPdfloading && (
        <DownloadPdfLoaderModal loading={downloadPdfloading} name={"Report"} />
      )}
    </div>
  );
};

export default ViewGeneralIR;